import { isEmpty, orderBy } from '@/helper/lodashHelper';
import axios from '@/axios';
import { $settings } from '@/provider/settings';
import localStorageHelper from '@/helper/localStorageHelper';

const getMerkmalType = function (classMeta, filteredOptions) {
  if (['DATE', 'TIME'].includes(classMeta.ATFOR)) {
    return classMeta.ATFOR.toLowerCase();
  }

  if (filteredOptions.length > 0) {
    return classMeta.ATEIN ? 'dropdown' : 'checkbox';
  }

  return 'input';
};

// TODO: This whole part will be moved to BE - see IW-1123
const convertToFe = async function (store, response, type, initial) {
  const cls = response.data.DATA;
  const navigation = [];
  const classification = [];
  const getClasses = async function () {
    const classInfos = [];

    await cls.CLASS_CLASSIFICATIONS.reduce(
      async (previous, objectClass, index) => {
        await previous;

        navigation.push(objectClass.KSCHL);

        const clsInfo = await store.dispatch('getClassInfo', {
          type,
          path: `${objectClass.KLART}/${objectClass.CLASS}`,
        });
        classInfos[index] = clsInfo;
      },
      Promise.resolve(),
    );

    return classInfos;
  };

  const classes = await getClasses();

  classes.forEach((classInfo) => {
    const classData = [];

    classInfo.CLINFO_MERKMALE.forEach((classMeta) => {
      const filteredOptions =
        classInfo.CLINFO_VALUES.filter(
          (classValue) => classValue.ATINN === classMeta.ATINN,
        ) || [];
      const itemInfo =
        classInfo.CLINFO_MERKM_VALUES.find(
          (classValue) => classValue.ATINN === classMeta.ATINN,
        ) || {};
      classMeta = { ...classMeta, ATWRT: itemInfo.ATWRT };

      const getClassItems = function () {
        const returnValue = {
          options: [],
          items: [],
          allocated: false,
          type: getMerkmalType(classMeta, filteredOptions),
        };

        switch (returnValue.type) {
          case 'dropdown': {
            returnValue.options = filteredOptions.map((classValue) => ({
              value: classValue.ATWRT,
              text: classValue.ATWTB,
            }));
            break;
          }
          case 'checkbox': {
            returnValue.items = filteredOptions.map((classValue) => ({
              value: classValue.ATWRT,
              originalValue: classValue.ATWRT,
              fallbackValue: classValue.ATSTD,
              model: false,
              text: classValue.ATWTB,
              type: returnValue.type,
              info: { ...classMeta, ...classValue },
            }));

            returnValue.items.forEach((item, index) => {
              const hasAllocatedValue =
                !!cls.CLASS_ALLOC_VALUES.some(
                  (allocValue) =>
                    allocValue.ATINN === classMeta.ATINN &&
                    allocValue.ATWRT === item.value,
                ) ||
                (initial && item.fallbackValue);

              returnValue.items[index].model = hasAllocatedValue || false;

              if (hasAllocatedValue) {
                returnValue.allocated = hasAllocatedValue;
              }
            });
            break;
          }
          default: {
            ('');
          }
        }

        if (returnValue.items.length === 0) {
          cls.CLASS_ALLOC_VALUES.filter(
            (currentAllocValue) => currentAllocValue.ATINN === classMeta.ATINN,
          ).forEach((allocValue) => {
            const charDescr = returnValue.options.find(
              (currentOption) => currentOption.value === allocValue.ATWRT,
            ) || {
              text: allocValue.ATWRT,
            };
            returnValue.items.push({
              model: allocValue.ATWRT,
              originalValue: allocValue.ATWRT,
              text: charDescr.text,
              type: returnValue.type,
              info: classMeta,
            });
            returnValue.allocated = true;
          });
        }

        // still has no items? Return default Object
        if (returnValue.items.length === 0) {
          let defaultValue = {};

          if (initial) {
            defaultValue =
              classInfo.CLINFO_VALUES.find(
                (allocValue) =>
                  allocValue.ATINN === classMeta.ATINN && !!allocValue.ATSTD,
              ) || {};
          }

          const { ATWRT = '', ATWRB = '' } = defaultValue;

          returnValue.items.push({
            model: ATWRT,
            text: ATWRB,
            info: classMeta,
          });
        }

        const required = !!returnValue.items.some((item) => item.info.ATERF);

        return { ...returnValue, required };
      };

      classData.push({
        title: classMeta.ATBEZ,
        position: classMeta.SORT_MERK,
        ...getClassItems(),
      });
    });

    classification.push(orderBy(classData, 'position'));
  });

  return { navigation, classification };
};

export default {
  getData(store, { type, key, editable }) {
    const cid = localStorageHelper.getCid();
    const useKey = key && key.split('/')[0];

    return axios({
      url: [
        $settings.middlewareURI,
        `exp/${cid}/${type}/classification`,
        useKey,
      ].join('/'),
    }).then((response) => {
      if (!response.data.SUCCESS) {
        return {};
      }

      return convertToFe(store, response, type).then((object) => {
        const endpoint = editable ? 'SET_EDIT_CLASS_DATA' : 'SET_CLASS_DATA';
        store.commit(endpoint, object);

        return object;
      });
    });
  },
  async initData(store, type) {
    const cid = localStorageHelper.getCid();
    const initData = store.state.initData[type];

    if (!isEmpty(initData)) {
      return initData;
    }

    const response = await axios({
      url: [
        $settings.middlewareURI,
        `exp/${cid}/${type}/classification/init`,
      ].join('/'),
    });

    if (!response.data.SUCCESS) {
      throw new Error('Error', JSON.stringify(response.data));
    }

    const object = await convertToFe(store, response, type, true);
    store.commit('SET_INIT_DATA', { type, data: object });
    store.commit('SET_EDIT_CLASS_DATA', object);

    return object;
  },
  updateData(store, { type, key }) {
    const classification = store.state.classDataEditDelta;

    if (isEmpty(classification)) return Promise.resolve();

    const cid = localStorageHelper.getCid();
    const useKey = key && key.split('/')[0];

    return axios({
      method: 'POST',
      url: [
        $settings.middlewareURI,
        `exp/${cid}/${type}/classification`,
        useKey,
      ].join('/'),
      data: {
        CLASS_ALLOC_VALUES: Object.values(classification),
      },
    }).then((response) => {
      if (!response.data.SUCCESS) {
        throw new Error('Error', JSON.stringify(response.data));
      }
    });
  },
  async getClassInfo(store, { type, path }) {
    const cid = localStorageHelper.getCid();
    return axios({
      url: [$settings.middlewareURI, `exp/${cid}/${type}/classinfo`, path].join(
        '/',
      ),
    }).then((response) => {
      if (!response.data.SUCCESS) {
        throw new Error('Error', JSON.stringify(response.data));
      }

      return response.data.DATA;
    });
  },
  setEditDelta(store, delta) {
    store.commit('SET_EDIT_DELTA', delta);
  },
  resetEditData(store) {
    store.commit('RESET_EDIT');
  },
};
